import React from "react";
import { GlobalOutlined, LinkedinOutlined, TwitterOutlined } from "@ant-design/icons";
import { PublicCareerSettings, hexAlpha } from "../../types";

interface HeroBannerProps {
  settings: PublicCareerSettings | null;
  searchInput: string;
  setSearchInput: (val: string) => void;
  setSearchQuery: (val: string) => void;
}

const HeroBanner: React.FC<HeroBannerProps> = ({
  settings,
  searchInput,
  setSearchInput,
  setSearchQuery,
}) => {
  const primary = settings?.primary_color || "#4f46e5";

  return (
    <div style={{
      position: "relative",
      height: 420,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    }}>
      
      {settings?.banner_image_url ? (
        <>
          <div style={{
            position: "absolute", inset: 0,
            backgroundImage: `url(${settings.banner_image_url})`,
            backgroundSize: "cover", backgroundPosition: "center",
            filter: "brightness(0.35)",
          }} />
          
          <div style={{
            position: "absolute", inset: 0,
            background: "linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.65) 100%)",
          }} />
        </>
      ) : (
        <>
          <div style={{
            position: "absolute", inset: 0,
            background: `linear-gradient(135deg, ${primary} 0%, #0d0d1a 100%)`,
          }} />
          
          <div style={{
            position: "absolute", width: 500, height: 500, borderRadius: "50%",
            background: "rgba(255,255,255,0.04)", top: -150, left: -150,
          }} />
          <div style={{
            position: "absolute", width: 350, height: 350, borderRadius: "50%",
            background: "rgba(255,255,255,0.06)", bottom: -80, right: -80,
          }} />
        </>
      )}

      
      <div style={{
        position: "relative", zIndex: 2,
        display: "flex", flexDirection: "column", alignItems: "center",
        textAlign: "center", padding: "0 24px", width: "100%", maxWidth: 720,
      }}>
        <h1 style={{
          color: "#fff", fontWeight: 800, fontSize: 40,
          letterSpacing: "-0.5px", lineHeight: 1.2,
          margin: "0 0 14px",
          textShadow: "0 2px 16px rgba(0,0,0,0.6)",
        }}>
          Careers at{" "}
          <span style={{ color: settings?.banner_image_url ? "#fff" : hexAlpha("#fff", 0.85) }}>
            {settings?.company_name}
          </span>
        </h1>

        <p style={{
          color: "rgba(255,255,255,0.88)", fontSize: 16, lineHeight: 1.7,
          margin: "0 0 24px", maxWidth: 580,
          textShadow: "0 1px 8px rgba(0,0,0,0.5)",
        }}>
          {settings?.about_company || "Explore open roles and join our team. We're building something great — come be part of it."}
        </p>

        
        {(settings?.social_links?.linkedin || settings?.social_links?.twitter || settings?.social_links?.glassdoor) && (
          <div style={{ display: "flex", gap: 14, marginBottom: 28 }}>
            {settings?.social_links?.linkedin && (
              <a href={settings.social_links.linkedin} target="_blank" rel="noreferrer"
                style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: 15, textDecoration: "none",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}>
                <LinkedinOutlined />
              </a>
            )}
            {settings?.social_links?.twitter && (
              <a href={settings.social_links.twitter} target="_blank" rel="noreferrer"
                style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: 15, textDecoration: "none",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}>
                <TwitterOutlined />
              </a>
            )}
            {settings?.social_links?.glassdoor && (
              <a href={settings.social_links.glassdoor} target="_blank" rel="noreferrer"
                style={{
                  width: 34, height: 34, borderRadius: "50%",
                  background: "rgba(255,255,255,0.15)", backdropFilter: "blur(8px)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#fff", fontSize: 15, textDecoration: "none",
                  border: "1px solid rgba(255,255,255,0.2)",
                }}>
                <GlobalOutlined />
              </a>
            )}
          </div>
        )}

        
        <div style={{
          display: "flex", alignItems: "center",
          background: "#fff", borderRadius: 50,
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          padding: "6px 6px 6px 18px",
          width: "100%", maxWidth: 580,
        }}>
          <i className="ti ti-search" style={{ color: "#aaa", fontSize: 16, marginRight: 10, flexShrink: 0 }} />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") setSearchQuery(searchInput); }}
            placeholder="Search by job title, department, or location..."
            style={{
              flex: 1, border: "none", outline: "none",
              fontSize: 14, color: "#333", background: "transparent",
            }}
          />
          <button
            onClick={() => setSearchQuery(searchInput)}
            style={{
              padding: "9px 24px", borderRadius: 50, border: "none",
              background: primary, color: "#fff",
              fontSize: 13, fontWeight: 600, cursor: "pointer",
              flexShrink: 0,
            }}
          >
            Search
          </button>
        </div>
      </div>
    </div>
  );
};

export default HeroBanner;
